import { conteudoDoQr } from '@/lib/qr';

/**
 * OS CÓDIGOS QUE AINDA NÃO TÊM MÁQUINA (pedido do dono, 31/10/2026).
 *
 * *"Tem que ter a possibilidade de fazer também de equipamentos que ainda não
 * temos cadastrado. Ex.: se temos até BAS-03 eu posso escolher fazer mais 3 e
 * ir até BAS-06."* O adesivo é feito na gráfica antes de a máquina chegar, e
 * o código que ele leva tem de ser O MESMO que o sistema vai criar quando ela
 * for cadastrada (0131: PREFIXO-NNN, o maior número do prefixo + 1, na
 * largura que a empresa já usa). Por isso a série segue a regra do banco: o
 * separador e a largura de quem já existe.
 *
 * ## Nunca um código que já existe
 *
 * Dois adesivos com o mesmo código são duas máquinas que o leitor confunde,
 * e o erro só aparece no pátio, com o adesivo colado. O código que já existe
 * (na frota, ou já posto no arquivo) RECUSA o lote inteiro, com a frase que
 * diz de onde recomeçar. `BAS-4` e `BAS-004` contam como o mesmo: o número é
 * que identifica a máquina na série.
 *
 * Funções puras, para terem teste; a tela só desenha.
 */

/** Um código decomposto: `BAS-003` é `BAS`, `-`, 3, largura 3. */
export interface CodigoEmSerie {
  readonly prefixo: string;
  readonly separador: string;
  readonly numero: number;
  readonly largura: number;
}

/** Uma série da frota: o prefixo, o separador, e até onde ela vai. */
export interface Serie {
  /** `BAS-`: o prefixo com o separador, que é o que identifica a série. */
  readonly chave: string;
  readonly prefixo: string;
  readonly separador: string;
  /** Os algarismos que a série usa (o maior que existe nela). */
  readonly largura: number;
  readonly maior: number;
  /** O código do maior número, como está escrito: `BAS-003`. */
  readonly ultimo: string;
  readonly quantidade: number;
}

/** O prefixo de um código novo, a mesma regra do banco (0131): só letras, de 2 a 5. */
const FORMATO_DO_PREFIXO = /^[A-Z]{2,5}$/;

/** Letras, um separador opcional e os algarismos do fim. */
const FORMATO_EM_SERIE = /^([A-Z]+)([-_. ]?)(\d+)$/;

/** O teto de códigos novos num lote: uma frota, não um catálogo. */
export const TETO_DO_LOTE = 200;

export function lerCodigoEmSerie(codigo: string): CodigoEmSerie | null {
  const m = FORMATO_EM_SERIE.exec(codigo.trim().toUpperCase());
  if (!m) return null;
  const [, prefixo = '', separador = '', algarismos = ''] = m;
  return { prefixo, separador, numero: Number(algarismos), largura: algarismos.length };
}

function montar(prefixo: string, separador: string, numero: number, largura: number): string {
  return `${prefixo}${separador}${String(numero).padStart(largura, '0')}`;
}

/** As séries da frota, da maior para a menor; empate, pelo prefixo. */
export function seriesDaFrota(codigos: readonly string[]): readonly Serie[] {
  const porChave = new Map<string, Serie>();
  for (const codigo of codigos) {
    const lido = lerCodigoEmSerie(codigo);
    if (lido === null) continue;
    const chave = `${lido.prefixo}${lido.separador}`;
    const antes = porChave.get(chave);
    const maiorAgora = antes === undefined || lido.numero > antes.maior;
    porChave.set(chave, {
      chave,
      prefixo: lido.prefixo,
      separador: lido.separador,
      largura: Math.max(antes?.largura ?? 0, lido.largura),
      maior: maiorAgora ? lido.numero : antes.maior,
      ultimo: maiorAgora ? codigo.trim().toUpperCase() : antes.ultimo,
      quantidade: (antes?.quantidade ?? 0) + 1,
    });
  }
  return [...porChave.values()].sort((a, b) => b.quantidade - a.quantidade || a.chave.localeCompare(b.chave));
}

/**
 * O que já está ocupado: o código escrito e o número dentro da série. Os dois
 * juntos pegam `BAS-004` contra `BAS-4` e também o código fora de série
 * (`CB07X`), que só se compara escrito.
 */
export interface Ocupados {
  readonly escritos: ReadonlySet<string>;
  readonly numeros: ReadonlySet<string>;
}

export function ocupadosDe(codigos: readonly string[]): Ocupados {
  const escritos = new Set<string>();
  const numeros = new Set<string>();
  for (const codigo of codigos) {
    const limpo = codigo.trim().toUpperCase();
    escritos.add(limpo);
    const lido = lerCodigoEmSerie(limpo);
    if (lido !== null) numeros.add(`${lido.prefixo}${lido.separador}#${String(lido.numero)}`);
  }
  return { escritos, numeros };
}

function ocupado(codigo: string, ocupados: Ocupados): boolean {
  if (ocupados.escritos.has(codigo)) return true;
  const lido = lerCodigoEmSerie(codigo);
  return lido !== null && ocupados.numeros.has(`${lido.prefixo}${lido.separador}#${String(lido.numero)}`);
}

export type Lote = { readonly codigos: readonly string[] } | { readonly erro: string };

/** Confere um lote inteiro: quantidade, forma de cada código e o que já existe. */
function conferirLote(codigos: readonly string[], ocupados: Ocupados, deOndeRecomecar: (codigo: string) => string): Lote {
  for (const codigo of codigos) {
    if (conteudoDoQr(codigo) === null) {
      return { erro: `${codigo} não cabe num adesivo: o código tem de 2 a 20 letras, algarismos e traço. Diminua os algarismos ou o prefixo.` };
    }
    if (ocupado(codigo, ocupados)) return { erro: deOndeRecomecar(codigo) };
  }
  return { codigos };
}

/** O primeiro número, a partir de `desde`, em que o lote INTEIRO cabe sem repetir código. */
function primeiroInicioLivre(prefixo: string, separador: string, desde: number, quantos: number, largura: number, ocupados: Ocupados): number {
  let n = desde;
  for (;;) {
    let i = 0;
    while (i < quantos && !ocupado(montar(prefixo, separador, n + i, largura), ocupados)) i += 1;
    if (i === quantos) return n;
    n += i + 1;
  }
}

function erroDeQuantidade(quantos: number): string | null {
  if (!Number.isInteger(quantos) || quantos < 1) return 'Informe quantos códigos a mais, de 1 em diante.';
  if (quantos > TETO_DO_LOTE) return `São no máximo ${String(TETO_DO_LOTE)} códigos por vez. Gere em partes.`;
  return null;
}

/**
 * Os próximos `quantos` da série, depois do maior que existe: BAS-003 e mais
 * 3 dá BAS-004, BAS-005 e BAS-006, no separador e na largura da série. O
 * maior conta também o que já foi posto no arquivo: o segundo "mais 3" segue
 * do BAS-007. A largura cresce sozinha quando o número não cabe (BAS-999 e
 * mais 1 é BAS-1000), como no banco.
 */
export function proximosDaSerie(serie: Serie, quantos: number, ocupados: Ocupados): Lote {
  const erro = erroDeQuantidade(quantos);
  if (erro !== null) return { erro };
  const daSerie = `${serie.chave}#`;
  const maior = [...ocupados.numeros].reduce(
    (m, n) => (n.startsWith(daSerie) ? Math.max(m, Number(n.slice(daSerie.length))) : m),
    serie.maior,
  );
  const codigos = Array.from({ length: quantos }, (_, i) => montar(serie.prefixo, serie.separador, maior + 1 + i, serie.largura));
  return conferirLote(codigos, ocupados, (codigo) => {
    const livre = primeiroInicioLivre(serie.prefixo, serie.separador, serie.maior + 1, quantos, serie.largura, ocupados);
    return `${codigo} já está no arquivo. A série segue livre a partir de ${montar(serie.prefixo, serie.separador, livre, serie.largura)}; retire o lote que tem ${codigo} ou gere a partir dali.`;
  });
}

export interface PrefixoNovo {
  readonly prefixo: string;
  readonly inicio: number;
  readonly quantidade: number;
  readonly algarismos: number;
}

/**
 * Um prefixo que a frota ainda não usa (ou uma série começada de outro
 * número): PREFIXO-NNN com traço, que é o que o sistema cria (0131).
 */
export function codigosDoPrefixoNovo(pedido: PrefixoNovo, ocupados: Ocupados): Lote {
  const prefixo = pedido.prefixo.trim().toUpperCase();
  if (!FORMATO_DO_PREFIXO.test(prefixo)) {
    return { erro: 'O prefixo tem de 2 a 5 letras, sem número nem acento: ESC, BAS, CAM.' };
  }
  if (!Number.isInteger(pedido.inicio) || pedido.inicio < 1) return { erro: 'Informe o número inicial, de 1 em diante.' };
  if (!Number.isInteger(pedido.algarismos) || pedido.algarismos < 1 || pedido.algarismos > 6) {
    return { erro: 'Informe de 1 a 6 algarismos. O sistema usa 3: ESC-007.' };
  }
  const erro = erroDeQuantidade(pedido.quantidade);
  if (erro !== null) return { erro };
  const codigos = Array.from({ length: pedido.quantidade }, (_, i) => montar(prefixo, '-', pedido.inicio + i, pedido.algarismos));
  return conferirLote(codigos, ocupados, (codigo) => {
    const livre = primeiroInicioLivre(prefixo, '-', pedido.inicio, pedido.quantidade, pedido.algarismos, ocupados);
    return `${codigo} já existe na frota ou no arquivo. Comece do ${montar(prefixo, '-', livre, pedido.algarismos)} ou escolha outro prefixo.`;
  });
}

/** Os códigos de um lote em uma linha curta: "BAS-004 a BAS-006", ou a lista quando são poucos. */
export function resumoDoLote(codigos: readonly string[]): string {
  if (codigos.length <= 3) return codigos.join(', ');
  return `${codigos[0] ?? ''} a ${codigos.at(-1) ?? ''}`;
}
